import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = 'https://deepxtechnologies.com';

const endpoints = [
  {
    category: 'PAN Verification',
    items: [
      {
        method: 'POST',
        path: '/api/verify/pan',
        title: 'Basic PAN Verification',
        description: 'Verify a PAN number and get basic details like name, PAN type, and validity status.',
        body: { pan: 'ABCDE1234F' },
        response: {
          success: true,
          message: 'PAN verification successful',
          data: {
            pan: 'ABCDE1234F',
            registered_name: 'RAHUL SHARMA',
            type: 'Individual',
            valid: true,
            name_match_result: 'EXACT_MATCH',
            pan_status: 'VALID'
          },
          creditsDeducted: 1
        }
      },
      {
        method: 'POST',
        path: '/api/verify/pan-360',
        title: 'PAN 360 (Advanced)',
        description: 'Comprehensive PAN verification with gender, DOB, Aadhaar link status, email, phone, and full address.',
        body: { pan: 'ABCDE1234F' },
        response: {
          success: true,
          message: 'PAN 360 verification successful',
          data: {
            pan: 'ABCDE1234F',
            first_name: 'RAHUL',
            last_name: 'SHARMA',
            registered_name: 'RAHUL SHARMA',
            type: 'Individual',
            gender: 'Male',
            date_of_birth: '1992-05-20',
            masked_aadhaar: 'XXXX XXXX 5678',
            aadhaar_linked: true,
            email: 'r***@gmail.com',
            mobile_number: '99XXXXXX12',
            address: { street: '45 Park Avenue', city: 'Pune', state: 'Maharashtra', pincode: '411001' },
            status: 'VALID'
          },
          creditsDeducted: 2
        }
      },
      {
        method: 'POST',
        path: '/api/verify/pan-to-tan',
        title: 'PAN to TAN',
        description: 'Find TAN number(s) linked to a given PAN.',
        body: { pan: 'ABCDE1234F' },
        response: {
          success: true,
          message: 'PAN to TAN verification successful',
          data: { pan: 'ABCDE1234F', tan: 'PNEA12345B', tan_count: 1, status: 'VALID' },
          creditsDeducted: 1
        }
      },
      {
        method: 'POST',
        path: '/api/verify/pan-to-cin',
        title: 'PAN to CIN',
        description: 'Find Corporate Identification Number (CIN) linked to a PAN.',
        body: { pan: 'AABCT1234E' },
        response: {
          success: true,
          data: { pan: 'AABCT1234E', cin: 'U74999MH2021PTC345678', company_name: 'SAMPLE TECH SOLUTIONS PVT LTD', company_type: 'Private Limited', company_status: 'Active', status: 'FOUND' },
          creditsDeducted: 2
        }
      }
    ]
  },
  {
    category: 'GST Verification',
    items: [
      {
        method: 'POST',
        path: '/api/verify/gstin',
        title: 'GSTIN Full Verification',
        description: 'Complete GSTIN verification with all details including filing history, HSN codes, jurisdictions, and return filing frequency.',
        body: { gstin: '27AABCT1234E1Z5' },
        response: {
          success: true,
          data: {
            gstin: '27AABCT1234E1Z5',
            legal_name: 'SAMPLE TECH SOLUTIONS PVT LTD',
            trade_name: 'SAMPLE TECH',
            registration_date: '01/04/2021',
            constitution: 'Private Limited Company',
            status: 'Active',
            taxpayer_type: 'Regular',
            principal_place_address: '2nd Floor, Tower B, Business Park, Hinjewadi, Pune, Maharashtra, 411057',
            admin_state: 'State - CBIC',
            admin_zone: 'PUNE',
            aadhaar_authenticated: 'Yes',
            filing_details: { gstr3b: [], gstr1: [] },
            hsn_goods: [],
            hsn_services: []
          },
          creditsDeducted: 3
        }
      },
      {
        method: 'POST',
        path: '/api/verify/gstin-lite',
        title: 'GSTIN Lite',
        description: 'Basic GSTIN verification with only essential fields: legal name, trade name, registration date, constitution, status, taxpayer type, and address.',
        body: { gstin: '27AABCT1234E1Z5' },
        response: {
          success: true,
          data: {
            gstin: '27AABCT1234E1Z5',
            legal_name: 'SAMPLE TECH SOLUTIONS PVT LTD',
            trade_name: 'SAMPLE TECH',
            registration_date: '01/04/2021',
            constitution: 'Private Limited Company',
            status: 'Active',
            taxpayer_type: 'Regular',
            principal_place_address: '2nd Floor, Tower B, Business Park, Hinjewadi, Pune, Maharashtra, 411057'
          },
          creditsDeducted: 1.5
        }
      },
      {
        method: 'POST',
        path: '/api/verify/gstin-by-pan',
        title: 'PAN to GSTIN Search',
        description: 'Find all GSTIN numbers linked to a PAN.',
        body: { pan: 'AABCT1234E' },
        response: {
          success: true,
          data: {
            pan: 'AABCT1234E',
            count: 2,
            gstins: [
              { gstin: '27AABCT1234E1Z5', status: 'Active', state: 'Maharashtra' },
              { gstin: '29AABCT1234E1Z3', status: 'Active', state: 'Karnataka' }
            ]
          },
          creditsDeducted: 2
        }
      }
    ]
  },
  {
    category: 'MSME / Udyam',
    items: [
      {
        method: 'POST',
        path: '/api/verify/msme',
        title: 'MSME Verification',
        description: 'Verify MSME/Udyam registration number and get full enterprise details from the government portal.',
        body: { urn: 'UDYAM-MH-01-0012345' },
        response: {
          success: true,
          data: {
            udyam_registration_number: 'UDYAM-MH-01-0012345',
            name_of_enterprise: 'SAMPLE ENTERPRISES',
            organisation_type: 'Proprietorship',
            major_activity: 'Manufacturing',
            social_category: 'General',
            date_of_incorporation: '10/06/2018',
            date_of_udyam_registration: '15/08/2020'
          },
          creditsDeducted: 2
        }
      },
      {
        method: 'POST',
        path: '/api/verify/pan-to-udyam',
        title: 'PAN to Udyam Full',
        description: 'Find Udyam registration linked to a PAN and get full enterprise details from the government portal.',
        body: { pan: 'ABCDE1234F' },
        response: {
          success: true,
          data: {
            pan: 'ABCDE1234F',
            udyam_reg_no: 'UDYAM-MH-01-0012345',
            status: 'FOUND',
            portal_data: { name_of_enterprise: 'SAMPLE ENTERPRISES', organisation_type: 'Proprietorship' }
          },
          creditsDeducted: 3
        }
      },
      {
        method: 'POST',
        path: '/api/verify/pan-to-udyam-lite',
        title: 'PAN to Udyam Lite',
        description: 'Find Udyam registration number linked to a PAN (no portal scraping, returns only the Udyam number).',
        body: { pan: 'ABCDE1234F' },
        response: {
          success: true,
          data: { pan: 'ABCDE1234F', udyam_reg_no: 'UDYAM-MH-01-0012345', status: 'FOUND' },
          creditsDeducted: 1.5
        }
      },
      {
        method: 'POST',
        path: '/api/verify/udyam',
        title: 'Udyam Verification',
        description: 'Verify Udyam Registration Number directly from the government portal.',
        body: { udyamNumber: 'UDYAM-MH-01-0012345' },
        response: {
          success: true,
          data: { udyam_registration_number: 'UDYAM-MH-01-0012345', name_of_enterprise: 'SAMPLE ENTERPRISES', organisation_type: 'Proprietorship' },
          creditsDeducted: 2
        }
      },
      {
        method: 'POST',
        path: '/api/verify/udyam-certificate',
        title: 'Udyam Certificate Download',
        description: 'Download the Udyam Registration Certificate as PDF. Response is a binary PDF file.',
        body: { udyamNumber: 'UDYAM-MH-01-0012345' },
        response: 'Binary PDF file (Content-Type: application/pdf)',
        note: 'Response is a binary PDF. Set responseType to "arraybuffer" or "blob" when calling.'
      }
    ]
  },
  {
    category: 'TAN Verification',
    items: [
      {
        method: 'POST',
        path: '/api/verify/tan',
        title: 'TAN Verification',
        description: 'Verify a TAN number and get deductor details including name, address, and contact information.',
        body: { tan: 'PNEA12345B' },
        response: {
          success: true,
          data: { tan: 'PNEA12345B', name: 'SAMPLE TECH SOLUTIONS PVT LTD', address_line1: '45 Park Avenue', state: 'MH', pincode: '411001', status: 'VALID' },
          creditsDeducted: 1
        }
      }
    ]
  },
  {
    category: 'Bank Verification',
    items: [
      {
        method: 'POST',
        path: '/api/verify/bank-account',
        title: 'Bank Account Verification',
        description: 'Verify bank account details with penny drop. Returns account holder name, bank details, and name match score.',
        body: { accountNumber: '9876543210', ifsc: 'SBIN0005678', name: 'RAHUL SHARMA' },
        response: {
          success: true,
          data: {
            account_number: '9876543210',
            ifsc: 'SBIN0005678',
            name_at_bank: 'RAHUL SHARMA',
            bank_name: 'State Bank of India',
            branch: 'Pune Camp',
            account_status: 'VALID',
            name_match_score: '100',
            name_match_result: 'EXACT_MATCH'
          },
          creditsDeducted: 1.5
        }
      }
    ]
  },
  {
    category: 'Identity Verification',
    items: [
      {
        method: 'POST',
        path: '/api/verify/driving-license',
        title: 'Driving License Verification',
        description: 'Verify a Driving License number with date of birth.',
        body: { dlNumber: 'MH0120190000001', dob: '1992-05-20' },
        response: {
          success: true,
          data: { dl_number: 'MH0120190000001', name: 'RAHUL SHARMA', status: 'VALID', dl_status: 'Active' },
          creditsDeducted: 2
        }
      },
      {
        method: 'POST',
        path: '/api/verify/voter-id',
        title: 'Voter ID Verification',
        description: 'Verify an EPIC (Voter ID) number.',
        body: { epicNumber: 'XYZ1234567' },
        response: {
          success: true,
          data: { epic_number: 'XYZ1234567', name: 'RAHUL SHARMA', age: 32, gender: 'Male', state: 'Maharashtra', status: 'VALID' },
          creditsDeducted: 1.5
        }
      },
      {
        method: 'POST',
        path: '/api/verify/passport',
        title: 'Passport Verification',
        description: 'Verify a passport using file number and date of birth.',
        body: { fileNumber: 'J1234567', dob: '1992-05-20' },
        response: {
          success: true,
          data: { file_number: 'J1234567', name: 'RAHUL SHARMA', status: 'VALID' },
          creditsDeducted: 2
        }
      }
    ]
  },
  {
    category: 'Vehicle & CIN',
    items: [
      {
        method: 'POST',
        path: '/api/verify/vehicle-rc',
        title: 'Vehicle RC Verification',
        description: 'Verify a vehicle registration number and get comprehensive details including owner, insurance, fitness, and more.',
        body: { regNo: 'MH12CD5678' },
        response: {
          success: true,
          data: {
            registration_number: 'MH12CD5678',
            owner_name: 'RAHUL SHARMA',
            vehicle_category: 'LMV',
            maker_model: 'HYUNDAI CRETA',
            fuel_type: 'Diesel',
            registration_date: '2022-03-15',
            rc_status: 'ACTIVE',
            insurance_upto: '2026-03-14'
          },
          creditsDeducted: 1.5
        }
      },
      {
        method: 'POST',
        path: '/api/verify/cin',
        title: 'CIN Verification',
        description: 'Verify a Corporate Identification Number and get company details including directors.',
        body: { cin: 'U74999MH2021PTC345678' },
        response: {
          success: true,
          data: { cin: 'U74999MH2021PTC345678', company_name: 'SAMPLE TECH SOLUTIONS PVT LTD', incorporation_date: '2021-04-01', cin_status: 'Active' },
          creditsDeducted: 2
        }
      }
    ]
  },
  {
    category: 'Employment',
    items: [
      {
        method: 'POST',
        path: '/api/verify/employment-360',
        title: 'Employment 360',
        description: 'Verify employment details using UAN (Universal Account Number). Returns employee details, employer info, PF filing, and bank details.',
        body: { uan: '101234567890' },
        response: {
          success: true,
          data: {
            uan: '101234567890',
            employee_name: 'RAHUL SHARMA',
            gender: 'Male',
            dob: '20-05-1992',
            establishment_name: 'SAMPLE TECH SOLUTIONS PVT LTD',
            joining_date: '15-07-2021',
            employed: true,
            pan: 'ABCDE1234F',
            pf_filing_details: []
          },
          creditsDeducted: 3
        }
      }
    ]
  }
];

const CodeBlock = ({ code, language = 'json' }) => {
  const formatted = typeof code === 'string' ? code : JSON.stringify(code, null, 2);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(formatted);
    toast.success('Copied to clipboard', { autoClose: 1500 });
  };

  return (
    <Box sx={{ position: 'relative', mt: 1 }}>
      <Tooltip title="Copy">
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{ position: 'absolute', top: 8, right: 8, color: '#aaa', '&:hover': { color: '#fff' } }}
        >
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
      <Box
        component="pre"
        sx={{
          bgcolor: '#1e1e2e',
          color: '#cdd6f4',
          p: 2,
          borderRadius: 1,
          overflow: 'auto',
          fontSize: '0.82rem',
          lineHeight: 1.6,
          fontFamily: '"JetBrains Mono", "Fira Code", Consolas, monospace',
          maxHeight: 400
        }}
      >
        {formatted}
      </Box>
    </Box>
  );
};

const EndpointCard = ({ endpoint }) => {
  const [expanded, setExpanded] = useState(false);

  const curlExample = `curl -X ${endpoint.method} ${API_BASE}${endpoint.path} \\
  -H "Content-Type: application/json" \\
  -H "X-Token-Id: YOUR_TOKEN_ID" \\
  -H "X-Token-Secret: YOUR_TOKEN_SECRET" \\
  -d '${JSON.stringify(endpoint.body)}'`;

  return (
    <Paper
      sx={{ mb: 2, overflow: 'hidden', border: '1px solid #e0e0e0', cursor: 'pointer', '&:hover': { borderColor: '#1976d2' } }}
      onClick={() => setExpanded(!expanded)}
      elevation={0}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2, gap: 2 }}>
        <Chip
          label={endpoint.method}
          size="small"
          sx={{
            bgcolor: '#1976d2',
            color: '#fff',
            fontWeight: 700,
            fontSize: '0.7rem',
            minWidth: 50
          }}
        />
        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: 500, color: '#333' }}>
          {endpoint.path}
        </Typography>
        <Typography sx={{ ml: 'auto', color: '#666', fontSize: '0.85rem' }}>
          {endpoint.title}
        </Typography>
      </Box>

      {expanded && (
        <Box sx={{ px: 3, pb: 3, borderTop: '1px solid #f0f0f0' }} onClick={(e) => e.stopPropagation()}>
          <Typography variant="body2" sx={{ mt: 2, mb: 2, color: '#555' }}>
            {endpoint.description}
          </Typography>

          {endpoint.note && (
            <Alert severity="info" sx={{ mb: 2 }}>{endpoint.note}</Alert>
          )}

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}>Request Body</Typography>
          <CodeBlock code={endpoint.body} />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5, fontWeight: 600 }}>cURL Example</Typography>
          <CodeBlock code={curlExample} language="bash" />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5, fontWeight: 600 }}>Response</Typography>
          <CodeBlock code={endpoint.response} />
        </Box>
      )}
    </Paper>
  );
};

const ApiDocs = () => {
  const [activeCategory, setActiveCategory] = useState(0);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
      <ToastContainer position="top-right" autoClose={2000} />
      
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a2e', color: '#fff', py: 4, px: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <img src="/DeepX Logo.jpeg" alt="KYC Suite" style={{ height: 40, borderRadius: 8 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              KYC Suite API
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ color: '#b0b0c0', mb: 3 }}>
            by DeepX Technologies — Comprehensive KYC verification API for PAN, GSTIN, Bank, Aadhaar, and more.
          </Typography>

          <Paper sx={{ bgcolor: '#16213e', p: 3, border: '1px solid #2a2a4a' }} elevation={0}>
            <Typography variant="subtitle2" sx={{ color: '#8be9fd', mb: 1, fontWeight: 600 }}>
              Authentication
            </Typography>
            <Typography variant="body2" sx={{ color: '#cdd6f4', mb: 2 }}>
              All requests require token-based authentication. Include these headers with every request:
            </Typography>
            <CodeBlock code={`X-Token-Id: TKN_your_token_id_here\nX-Token-Secret: your_token_secret_here`} />
            <Typography variant="body2" sx={{ color: '#888', mt: 2 }}>
              Generate your tokens from the Profile page after logging in. Each API call deducts credits from your account based on the service pricing.
            </Typography>
          </Paper>

          <Paper sx={{ bgcolor: '#16213e', p: 3, mt: 2, border: '1px solid #2a2a4a' }} elevation={0}>
            <Typography variant="subtitle2" sx={{ color: '#8be9fd', mb: 1, fontWeight: 600 }}>
              Base URL
            </Typography>
            <Box sx={{ fontFamily: 'monospace', color: '#50fa7b', fontSize: '1rem' }}>
              {API_BASE}
            </Box>
            <Typography variant="body2" sx={{ color: '#888', mt: 1 }}>
              All endpoints are relative to this base URL. Content-Type must be <code style={{ color: '#f8f8f2' }}>application/json</code>.
            </Typography>
          </Paper>

          <Paper sx={{ bgcolor: '#16213e', p: 3, mt: 2, border: '1px solid #2a2a4a' }} elevation={0}>
            <Typography variant="subtitle2" sx={{ color: '#8be9fd', mb: 1, fontWeight: 600 }}>
              Error Handling
            </Typography>
            <Typography variant="body2" sx={{ color: '#cdd6f4', mb: 1 }}>
              All errors follow this structure:
            </Typography>
            <CodeBlock code={{ success: false, message: 'Descriptive error message', code: 'ERROR_CODE' }} />
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: '#888' }}>
                <strong>401</strong> — Invalid or missing credentials&nbsp;&nbsp;|&nbsp;&nbsp;
                <strong>402</strong> — Insufficient credits&nbsp;&nbsp;|&nbsp;&nbsp;
                <strong>403</strong> — IP not whitelisted / Account inactive&nbsp;&nbsp;|&nbsp;&nbsp;
                <strong>400</strong> — Invalid input&nbsp;&nbsp;|&nbsp;&nbsp;
                <strong>500</strong> — Server error
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Tabs
          value={activeCategory}
          onChange={(e, v) => setActiveCategory(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3, borderBottom: '1px solid #e0e0e0' }}
        >
          {endpoints.map((cat, i) => (
            <Tab key={i} label={cat.category} sx={{ fontWeight: 600, textTransform: 'none' }} />
          ))}
        </Tabs>

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          {endpoints[activeCategory].category}
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          {endpoints[activeCategory].items.length} endpoint{endpoints[activeCategory].items.length > 1 ? 's' : ''}
        </Typography>

        {endpoints[activeCategory].items.map((ep, i) => (
          <EndpointCard key={i} endpoint={ep} />
        ))}
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: '#1a1a2e', color: '#888', py: 3, textAlign: 'center' }}>
        <Typography variant="body2">
          KYC Suite API v1.0 — DeepX Technologies
        </Typography>
      </Box>
    </Box>
  );
};

export default ApiDocs;
